'use client';
/**
 * 动态获取商品数据
 * 用于在组件挂载时获取商品数据，支持预览模式和正常模式
 * @param params
 * @returns 动态获取商品数据的函数
 * @template T 商品数据类型
 * @param params.componentId 组件ID
 * @param params.componentBlockId 组件BlockID
 * @param params.currencyName 货币名称
 * @param params.enableAuthRefetch 是否启用认证刷新
 * @param params.dynamicProductsData 商品数据
 * @param params.hasAttemptedFetch 是否已尝试过获取数据
 * @param params.isLoading 是否正在加载数据
 * @param params.setDynamicProductsData 设置商品数据的函数
 * @param params.setIsLoading 设置加载状态的函数
 * @param params.setHasAttemptedFetch 设置已尝试过获取数据的函数
 * @returns 动态获取商品数据的函数
 */

import { getComponentProductData } from '@/api/home';
import { diffProductData } from '@/app/(home-page)/utils/diffProducts';
import { useWhenTokenReady } from '@/hooks/useWhenReady';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { HOME_PREVIEW_PATH } from '@/utils/constants';
import { useCallback, useRef } from 'react';

interface UseFetchDynamicProductsParams<T> {
  componentId?: string;
  componentBlockId?: string | number;
  currencyName?: string;
  enableAuthRefetch?: boolean;
  /** 开启后，后台刷新时仅当数据真正变化才更新状态，避免骨架屏闪现 */
  enableDiffUpdate?: boolean;
  dynamicProductsData: T | null;
  hasAttemptedFetch: boolean;
  isLoading: boolean;
  setDynamicProductsData: React.Dispatch<React.SetStateAction<T | null>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setHasAttemptedFetch: React.Dispatch<React.SetStateAction<boolean>>;
}

export function useFetchDynamicProducts<T>({
  componentId,
  componentBlockId,
  currencyName,
  enableAuthRefetch = true,
  enableDiffUpdate = false,
  dynamicProductsData,
  hasAttemptedFetch,
  isLoading,
  setDynamicProductsData,
  setIsLoading,
  setHasAttemptedFetch,
}: UseFetchDynamicProductsParams<T>) {
  const token = useStore((state) => state.token);
  const isLoggedIn = !!token;
  const authStatusRef = useRef(isLoggedIn);
  // 防止并发请求 & 丢弃过期响应
  const fetchingRef = useRef(false);
  const requestIdRef = useRef(0);
  // 请求在飞期间登录态变化，结束后补发
  const pendingAuthRefreshRef = useRef(false);

  const fetchDynamicProducts = useCallback(async () => {
    if (!componentId || !componentBlockId) {
      return;
    }

    // 防重入：已有请求在飞，记录待补发
    if (fetchingRef.current) {
      pendingAuthRefreshRef.current = true;
      return;
    }

    const isPreview =
      typeof window !== 'undefined' && window.location.pathname.includes(HOME_PREVIEW_PATH);

    const isBackgroundRefresh = enableDiffUpdate && dynamicProductsData !== null;
    const currentRequestId = ++requestIdRef.current;
    fetchingRef.current = true;

    // 后台刷新时不设置 loading，避免组件显示骨架屏
    if (!isBackgroundRefresh) {
      setIsLoading(true);
    }

    try {
      const res: any = await getComponentProductData({
        component_id: componentId,
        id: Number(componentBlockId),
        currency: currencyName || 'USD',
        status: isPreview ? 2 : 1,
      });

      // 丢弃过期响应：已有更新的请求在飞
      if (currentRequestId !== requestIdRef.current) {
        return;
      }

      if (res && typeof res !== 'string' && res.data) {
        if (isBackgroundRefresh) {
          const { changed, mergedData } = diffProductData(dynamicProductsData, res.data);
          if (changed && mergedData) {
            setDynamicProductsData(mergedData);
          }
        } else {
          setDynamicProductsData(res.data);
        }
      } else if (!isBackgroundRefresh) {
        setDynamicProductsData(null);
      }
    } catch (err) {
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      logger.error('获取商品数据失败:', err);
      if (!isBackgroundRefresh) {
        setDynamicProductsData(null);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        fetchingRef.current = false;
        if (!isBackgroundRefresh) {
          setIsLoading(false);
        }
        setHasAttemptedFetch(true);

        // 补发因请求在飞而被跳过的登录态刷新
        if (pendingAuthRefreshRef.current) {
          pendingAuthRefreshRef.current = false;
          // 异步递归，避免 finally 栈帧过长
          Promise.resolve().then(() => fetchDynamicProducts());
        }
      }
    }
  }, [
    componentBlockId,
    componentId,
    currencyName,
    dynamicProductsData,
    enableDiffUpdate,
    setDynamicProductsData,
    setHasAttemptedFetch,
    setIsLoading,
  ]);

  useWhenTokenReady(() => {
    if (authStatusRef.current === isLoggedIn) {
      return;
    }

    authStatusRef.current = isLoggedIn;

    if (!enableAuthRefetch) {
      return;
    }

    if (!hasAttemptedFetch || !dynamicProductsData || isLoading) {
      return;
    }

    // 请求在飞时记录待补发，不跳过
    if (fetchingRef.current) {
      pendingAuthRefreshRef.current = true;
      return;
    }

    fetchDynamicProducts();
  }, [
    dynamicProductsData,
    enableAuthRefetch,
    fetchDynamicProducts,
    hasAttemptedFetch,
    isLoading,
    isLoggedIn,
  ]);

  return fetchDynamicProducts;
}
